import React, { useEffect, useState, useRef } from 'react';
import { Toggle, Notification, InputGroup, Input, toaster } from 'rsuite';
import UpdateButton from "../../../../../shared/UpdateButton/UpdateButton";
import { extractTimezoneFromISO, formatTimeWithoutSeconds } from "../utils/Dimmer.utils";
import { TfiSave } from "react-icons/tfi";
import { SharedApi } from "../../../../../../services/Shared.api";
import { formatTimezoneOffset } from "../../../../../../shared/Time.utils";
import moment from "moment/moment";
import DateTimeEditModal from "../../../../../modals/DateTimeEditModal/DateTimeEditModal";
import { VscEdit } from "react-icons/vsc";
import {clockFaceSrc, hourHandSrc, minuteHandSrc, secondHandSrc} from "../../../../SecondaryClock/SecondaryClock.model";

const DimmerDeviceDataSection = ({ deviceId, duskDawnScheduler, onRefreshDuskDawnScheduler }) => {
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deviceTime, setDeviceTime] = useState(null);
  const [userTimezone, setUserTimezone] = useState('+3');
  const [currentUtcDateTime, setCurrentUtcDateTime] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const timeMomentRef = useRef(moment());
  const intervalRef = useRef(null);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);

  useEffect(() => {
    getTime().then();
  }, [deviceId]);

  const getCurrentUtcDateTime = () => {
    return moment().utc().format('DD.MM.YYYY HH:mm:ss');
  };

  const getClockAnglesFromCurrentTime = () => {
    if (!currentTime) {
      return { hourAngle: 0, minuteAngle: 0, secondAngle: 0 };
    }

    const m = moment(currentTime, 'DD.MM.YYYY HH:mm:ss');
    if (!m.isValid()) {
      return { hourAngle: 0, minuteAngle: 0, secondAngle: 0 };
    }

    const hours = m.hour();
    const minutes = m.minute();
    const seconds = m.second();

    const hourIn12 = hours % 12;

    const hourAngle = hourIn12 * 30 + minutes * 0.5;
    const minuteAngle = minutes * 6;
    const secondAngle = seconds * 6; // 360 / 60

    return { hourAngle, minuteAngle, secondAngle };
  };


  const getTimeMoment = (date) => {
    if (!date) return moment();

    try {
      if (typeof date === 'string') {
        return moment(date, 'DD.MM.YYYY HH:mm:ss');
      }

      if (date instanceof Date) {
        return moment(date);
      }

      if (moment.isMoment(date)) {
        return date.clone();
      }

      return moment();
    } catch (error) {
      console.error('Error creating moment:', error);
      return moment();
    }
  };

  const updateTimeFromDeviceTime = () => {
    if (deviceTime) {
      const m = getTimeMoment(deviceTime);
      timeMomentRef.current = m;
      setCurrentTime(m.format('DD.MM.YYYY HH:mm:ss'));
    }
  };

  useEffect(() => {
    setCurrentUtcDateTime(getCurrentUtcDateTime());
    const utcIntervalId = setInterval(() => {
      setCurrentUtcDateTime(getCurrentUtcDateTime());
    }, 1000);

    return () => clearInterval(utcIntervalId);
  }, []);

  useEffect(() => {
    updateTimeFromDeviceTime();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (timeMomentRef.current) {
        timeMomentRef.current.add(1, 'seconds');
        setCurrentTime(timeMomentRef.current.format('DD.MM.YYYY HH:mm:ss'));
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [deviceTime]);

  const successMessage = (
    <Notification type={"success"} closable>
      <p>Настройки сохранены</p>
    </Notification>
  );

  const errorMessage = (
    <Notification type={"error"} closable>
      <p>Ошибка</p>
    </Notification>
  );

  const onSaveUTCTime = async () => {
    setLoading(true);
    try {
      await SharedApi.updateUTCTime(deviceId, deviceTime);
      toaster.push(successMessage, { placement: "topEnd" });
      await getTime();
    } catch (e) {
      console.error('Ошибка при сохранении времени:', e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const onSaveTimezone = async () => {
    setLoading(true);
    try {
      const formattedTimezone = formatTimezoneOffset(userTimezone);
      await SharedApi.updateTimezone(deviceId, formattedTimezone);
      toaster.push(successMessage, { placement: "topEnd" });
      await getTime();
    } catch (e) {
      console.error('Ошибка при сохранении времени:', e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const getTime = async () => {
    setLoading(true);

    try {
      const data = await SharedApi.getTime(deviceId);
      setDeviceData(data);

      if (data.time) {
        const timezone = extractTimezoneFromISO(data.time);
        setUserTimezone(timezone);

        const match = data.time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [_, year, month, day, hours, minutes, seconds] = match;

          const date = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds)
          );

          setDeviceTime(date);
          const m = getTimeMoment(date);
          timeMomentRef.current = m;
          setCurrentTime(m.format('DD.MM.YYYY HH:mm:ss'));
        } else {
          const date = new Date(data.time);
          setDeviceTime(date);
          const m = getTimeMoment(date);
          timeMomentRef.current = m;
          setCurrentTime(m.format('DD.MM.YYYY HH:mm:ss'));
        }
      }

    } catch (e) {
      console.error('Ошибка получения данных:', e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const updateDaylightSaving = async (enabled) => {
    setLoading(true);
    try {
      const result = await SharedApi.updateDaylightSaving(deviceId, enabled);

      if (result) {
        setDeviceData(prev => ({
          ...prev,
          isDaylightSavingEnabled: enabled
        }));
        toaster.push(successMessage, { placement: "topEnd" });
      } else {
        console.error('Ошибка при обновлении настроек');
      }
    } catch (e) {
      console.error('Ошибка при обновлении настроек:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDaylightSaving = async (enabled) => {
    await updateDaylightSaving(enabled);
  };

  const getSunriseSunset = () => {
    if (!duskDawnScheduler || duskDawnScheduler.length < 2) {
      return 'Нет данных';
    }

    const offTime = formatTimeWithoutSeconds(duskDawnScheduler[0]);
    const onTime = formatTimeWithoutSeconds(duskDawnScheduler[1]);

    return `${offTime} / ${onTime}`;
  };

  const handleRefreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        getTime(),
        onRefreshDuskDawnScheduler ? onRefreshDuskDawnScheduler() : Promise.resolve()
      ]);
    } catch (error) {
      console.error('Ошибка при обновлении данных:', error);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = (value) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  return (
    <>
      <section>
        <div className="row">
          <div className="col-md-12 text-end mb-2 mt-1">
            <UpdateButton onRefresh={() => handleRefreshAll()} />
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="clock-wrapper w-100 d-flex justify-content-center">
              {(() => {
                const { hourAngle, minuteAngle, secondAngle } = getClockAnglesFromCurrentTime();
                return (
                  <div className="clock-container pt-0">
                    <div className="clock" style={{ position: 'relative' }}>
                      <img
                        className="clock-face"
                        src={clockFaceSrc}
                        alt="clock face"
                      />
                      <img
                        className="clock-hand hour-hand"
                        src={hourHandSrc}
                        alt="hour hand"
                        style={{ transform: `rotate(${hourAngle}deg)` }}
                      />
                      <img
                        className="clock-hand minute-hand"
                        src={minuteHandSrc}
                        alt="minute hand"
                        style={{ transform: `rotate(${minuteAngle}deg)` }}
                      />
                      <img
                        className="clock-hand second-hand"
                        src={secondHandSrc}
                        alt="second hand"
                        style={{ transform: `rotate(${secondAngle}deg)` }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 mb-1">
            <div className="section-text time-wrapper">
              <InputGroup style={{ width: '100%' }}>
                <InputGroup.Addon>Время и дата</InputGroup.Addon>
                <Input
                  readOnly
                  value={currentTime}
                />
                <InputGroup.Addon>
                  <VscEdit
                    onClick={() => setIsTimeModalOpen(true)}
                    style={{ cursor: 'pointer' }}
                    title="Редактировать"
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Час. пояс</InputGroup.Addon>
              <Input
                value={userTimezone}
                onChange={handleTimezoneChange}
                style={{ textAlign: 'center' }}
              />
              <InputGroup.Addon>
                <TfiSave
                  style={{ cursor: 'pointer' }}
                  onClick={onSaveTimezone}
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Восход - Закат</InputGroup.Addon>
              <Input
                value={getSunriseSunset()}
                style={{ textAlign: 'center' }}
              />
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Время и дата UTC</InputGroup.Addon>
              <Input
                className="bg-yellow"
                readOnly={true}
                value={currentUtcDateTime}
              />
            </InputGroup>
          </div>
          <div className="col-md-12 mt-3">
            <Toggle
              size={"sm"}
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={deviceData?.isDaylightSavingEnabled || false}
              onChange={handleToggleDaylightSaving}
              disabled={loading}
              loading={loading}
            >
              Авт. переход зима / лето
            </Toggle>
          </div>
        </div>
      </section>

      <DateTimeEditModal
        open={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        initialValue={deviceTime}
        loading={loading}
        title="Редактировать дату и время"
        onSave={async (newMoment) => {
          const newDate = newMoment.toDate();

          setDeviceTime(newDate);
          const m = getTimeMoment(newDate);
          timeMomentRef.current = m;
          setCurrentTime(m.format('DD.MM.YYYY HH:mm:ss'));

          await onSaveUTCTime();
          setIsTimeModalOpen(false);
        }}
      />
    </>
  );
};

export default DimmerDeviceDataSection;
